import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem, Divider,
  IconButton, Checkbox, FormControlLabel, Grid, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Swal from "sweetalert2";
import {
  obterFormulario, salvarFormulario, TIPOS_RESPOSTA, tipoUsaOpcoes,
} from "../../services/formularioService";

const novaPergunta = () => ({
  Id: 0, Codigo: "", Secao: "", Enunciado: "", TipoResposta: "texto",
  Obrigatoria: false, Ativo: true, DependeDeCodigo: "", DependeDeValor: "", Opcoes: [],
});
const novaOpcao = () => ({ Id: 0, Valor: "", Rotulo: "" });

export default function FormularioBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ Id: 0, Nome: "", Versao: 1, Ativo: true });
  const [perguntas, setPerguntas] = useState([]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await obterFormulario(id);
      setForm({ Id: data.Id, Nome: data.Nome, Versao: data.Versao, Ativo: data.Ativo });
      setPerguntas(data.Perguntas || []);
    })();
  }, [id]);

  // ---- helpers de perguntas ----
  const updPergunta = (i, campo, valor) =>
    setPerguntas((ps) => ps.map((p, idx) => (idx === i ? { ...p, [campo]: valor } : p)));
  const addPergunta = () => setPerguntas((ps) => [...ps, novaPergunta()]);
  const delPergunta = (i) => setPerguntas((ps) => ps.filter((_, idx) => idx !== i));
  const mover = (i, dir) =>
    setPerguntas((ps) => {
      const j = i + dir;
      if (j < 0 || j >= ps.length) return ps;
      const copia = [...ps];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return copia;
    });

  // ---- helpers de opções ----
  const updOpcao = (pi, oi, campo, valor) =>
    setPerguntas((ps) => ps.map((p, idx) =>
      idx === pi
        ? { ...p, Opcoes: p.Opcoes.map((o, k) => (k === oi ? { ...o, [campo]: valor } : o)) }
        : p));
  const addOpcao = (pi) =>
    setPerguntas((ps) => ps.map((p, idx) => (idx === pi ? { ...p, Opcoes: [...p.Opcoes, novaOpcao()] } : p)));
  const delOpcao = (pi, oi) =>
    setPerguntas((ps) => ps.map((p, idx) =>
      idx === pi ? { ...p, Opcoes: p.Opcoes.filter((_, k) => k !== oi) } : p));

  const salvar = async () => {
    if (!form.Nome.trim()) return Swal.fire("Atenção", "Informe o nome do formulário.", "warning");
    const semCodigo = perguntas.find((p) => !p.Codigo.trim());
    if (semCodigo) return Swal.fire("Atenção", "Toda pergunta precisa de um código.", "warning");

    try {
      const dto = { ...form, Perguntas: perguntas };
      const { data } = await salvarFormulario(dto);
      await Swal.fire("Sucesso", data.mensagem, "success");
      navigate("/formularios");
    } catch (e) {
      Swal.fire("Erro", e?.response?.data || e.message, "error");
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5">{id ? `Formulário #${id}` : "Novo formulário"}</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate("/formularios")}>Voltar</Button>
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField fullWidth label="Nome do formulário" value={form.Nome}
              onChange={(e) => setForm({ ...form, Nome: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField fullWidth type="number" label="Versão" value={form.Versao}
              onChange={(e) => setForm({ ...form, Versao: Number(e.target.value) })} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={form.Ativo} onChange={(e) => setForm({ ...form, Ativo: e.target.checked })} />}
              label="Ativo" />
          </Grid>
        </Grid>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Perguntas ({perguntas.length})</Typography>
        <Button startIcon={<AddIcon />} onClick={addPergunta}>Adicionar pergunta</Button>
      </Stack>

      {perguntas.map((p, i) => (
        <Paper key={i} sx={{ p: 2, mb: 2 }} variant="outlined">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="caption" color="text.secondary">#{i + 1}</Typography>
            <Stack direction="row">
              <Tooltip title="Subir"><span>
                <IconButton size="small" disabled={i === 0} onClick={() => mover(i, -1)}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton></span></Tooltip>
              <Tooltip title="Descer"><span>
                <IconButton size="small" disabled={i === perguntas.length - 1} onClick={() => mover(i, 1)}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton></span></Tooltip>
              <IconButton size="small" color="error" onClick={() => delPergunta(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Enunciado" value={p.Enunciado}
                onChange={(e) => updPergunta(i, "Enunciado", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Código (único)" value={p.Codigo}
                onChange={(e) => updPergunta(i, "Codigo", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Seção" value={p.Secao || ""}
                onChange={(e) => updPergunta(i, "Secao", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField select fullWidth size="small" label="Tipo de resposta" value={p.TipoResposta}
                onChange={(e) => updPergunta(i, "TipoResposta", e.target.value)}>
                {TIPOS_RESPOSTA.map((t) => <MenuItem key={t.valor} value={t.valor}>{t.rotulo}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <FormControlLabel
                control={<Checkbox checked={p.Obrigatoria}
                  onChange={(e) => updPergunta(i, "Obrigatoria", e.target.checked)} />}
                label="Obrigatória" />
            </Grid>
          </Grid>

          {tipoUsaOpcoes(p.TipoResposta) && (
            <Box sx={{ mt: 1, pl: 2, borderLeft: "3px solid #eee" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption">Opções</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addOpcao(i)}>Opção</Button>
              </Stack>
              {p.Opcoes.map((o, oi) => (
                <Stack key={oi} direction="row" spacing={1} mt={1} alignItems="center">
                  <TextField size="small" label="Valor (código)" value={o.Valor}
                    onChange={(e) => updOpcao(i, oi, "Valor", e.target.value)} sx={{ width: 180 }} />
                  <TextField size="small" label="Rótulo" value={o.Rotulo}
                    onChange={(e) => updOpcao(i, oi, "Rotulo", e.target.value)} sx={{ flex: 1 }} />
                  <IconButton size="small" color="error" onClick={() => delOpcao(i, oi)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              {p.Opcoes.length === 0 && (
                <Typography variant="caption" color="text.secondary">Nenhuma opção adicionada.</Typography>
              )}
            </Box>
          )}

          {/* Condicional: exibir esta pergunta só quando outra tiver certo valor */}
          <Box sx={{ mt: 1.5, pl: 2, borderLeft: "3px solid #e3e8ff" }}>
            <Typography variant="caption" color="text.secondary">Exibir condicionalmente (opcional)</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={0.5}>
              <TextField select size="small" label="Depende da pergunta" sx={{ flex: 1 }}
                value={p.DependeDeCodigo || ""}
                onChange={(e) => { updPergunta(i, "DependeDeCodigo", e.target.value); updPergunta(i, "DependeDeValor", ""); }}>
                <MenuItem value="">— Sempre visível —</MenuItem>
                {perguntas.filter((q, qi) => qi !== i && q.Codigo).map((q, qi) => (
                  <MenuItem key={qi} value={q.Codigo}>{q.Enunciado || q.Codigo}</MenuItem>
                ))}
              </TextField>
              {p.DependeDeCodigo && (() => {
                const ctrl = perguntas.find((q) => q.Codigo === p.DependeDeCodigo);
                if (ctrl?.TipoResposta === "bool")
                  return (
                    <TextField select size="small" label="Quando for" sx={{ width: 170 }} value={p.DependeDeValor || ""} onChange={(e) => updPergunta(i, "DependeDeValor", e.target.value)}>
                      <MenuItem value="true">Sim (marcado)</MenuItem><MenuItem value="false">Não</MenuItem>
                    </TextField>
                  );
                if (ctrl && tipoUsaOpcoes(ctrl.TipoResposta))
                  return (
                    <TextField select size="small" label="Quando for" sx={{ width: 220 }} value={p.DependeDeValor || ""} onChange={(e) => updPergunta(i, "DependeDeValor", e.target.value)}>
                      {ctrl.Opcoes.map((o, oi) => <MenuItem key={oi} value={o.Valor}>{o.Rotulo || o.Valor}</MenuItem>)}
                    </TextField>
                  );
                return <TextField size="small" label="Quando for igual a" sx={{ width: 200 }} value={p.DependeDeValor || ""} onChange={(e) => updPergunta(i, "DependeDeValor", e.target.value)} />;
              })()}
            </Stack>
          </Box>

          <Divider sx={{ mt: 1 }} />
        </Paper>
      ))}

      {perguntas.length === 0 && (
        <Paper sx={{ p: 3, textAlign: "center" }} variant="outlined">
          <Typography color="text.secondary">Nenhuma pergunta. Clique em “Adicionar pergunta”.</Typography>
        </Paper>
      )}
    </Box>
  );
}
